import { RichTextField, TextField } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import { Button } from '@gn-audio/jss-components/components';
import { mapSharedPropertiesToFields } from '@gn-audio/jss-components/getters';
import { ComponentProps } from '@gn-audio/jss-components/lib/component-props';
import { SitecoreFieldTypes } from '@gn-audio/jss-components/types';
import { getContrastThemeClass, resolveBackgroundStyle } from '@gn-audio/jss-components/utils';

import { Eyebrow } from '../../../common/components/Eyebrow';
import PromoItem from './DualColumnPromoItem';
import {
  DualColumnPromoItem,
  FirstButtonSettings,
  SecondButtonSettings,
} from './dualColumnPromoTypes';

type DualColumnPromoSpot = {
  eyebrowText: TextField;
  headline: RichTextField;
  description: RichTextField;
  backgroundColor: TextField;
  promoItems?: DualColumnPromoItem[];
} & FirstButtonSettings &
  SecondButtonSettings;

type DualColumnPromoSpotProps = {
  fields: DualColumnPromoSpot;
} & ComponentProps;

const DualColumnPromoSpot: React.FC<DualColumnPromoSpotProps> = ({ fields }) => {
  const {
    eyebrowText,
    headline,
    description,
    backgroundColor,
    firstButtonLink,
    firstButtonText,
    firstButtonVariant,
    secondButtonLink,
    secondButtonText,
    secondButtonVariant,
    promoItems,
  } = mapSharedPropertiesToFields<DualColumnPromoSpot>(fields as SitecoreFieldTypes);

  const spotThemeClass = getContrastThemeClass(backgroundColor.value as string);
  const spotBackgroundStyle = resolveBackgroundStyle(backgroundColor.value as string);
  const isAnyMainButtonPresent =
    (!!firstButtonLink.value?.href && !!firstButtonText.value) ||
    (!!secondButtonLink.value?.href && !!secondButtonText.value);
  const isContentPresentAbovePromoItems =
    !!eyebrowText.value || !!headline.value || !!description.value || isAnyMainButtonPresent;

  return (
    <div style={spotBackgroundStyle} className={spotThemeClass}>
      <article className="container flex flex-col items-start">
        <Eyebrow className="mb-6" text={eyebrowText.value as string} />
        {!!headline.value && (
          <h2
            className="heading-2 mb-6"
            dangerouslySetInnerHTML={{ __html: sanitize(headline.value as string) }}
          />
        )}
        {!!description.value && (
          <p
            className="paragraph-large mb-6"
            dangerouslySetInnerHTML={{ __html: sanitize(description.value as string) }}
          />
        )}
        {isAnyMainButtonPresent && (
          <section className="mb-6 flex flex-col flex-wrap gap-4 md:flex-row">
            {!!firstButtonLink.value?.href && !!firstButtonText.value && (
              <Button
                asLink
                href={firstButtonLink.value.href}
                size="l"
                variant={firstButtonVariant.value || 'primary'}
              >
                {firstButtonText.value}
              </Button>
            )}
            {!!secondButtonLink.value?.href && !!secondButtonText.value && (
              <Button
                asLink
                href={secondButtonLink.value.href}
                size="l"
                variant={secondButtonVariant.value || 'secondary'}
              >
                {secondButtonText.value}
              </Button>
            )}
          </section>
        )}
        {!!promoItems?.length && (
          <div
            className={classNames('flex w-full flex-col gap-8 md:grid md:grid-cols-2', {
              'mt-6': isContentPresentAbovePromoItems,
            })}
          >
            {promoItems.map((item) => (
              <PromoItem key={item.id} item={item} />
            ))}
          </div>
        )}
      </article>
    </div>
  );
};
export default DualColumnPromoSpot;
